import { AddIcon } from '@chakra-ui/icons';
import {
    Box,
    ButtonProps,
    Checkbox,
    Divider,
    Drawer,
    DrawerContent,
    Flex,
    IconButton,
    Tooltip,
    useColorMode,
    useDisclosure,
    VStack,
} from '@chakra-ui/react';
import { cloneDeep } from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useState } from 'react';
import { useCurrentColors } from '../../../hooks/UseCurrentColors/UseCurrentColors';
import CanopyButton from '../../CanopyButton/CanopyButton';
import CanopySelect from '../../CanopySelect/CanopySelect';
import DrawerHeader from '../../Drawer/CustomDrawerHeader';
import ActFilter from '../../Icons/ActFilter';
type OnApply = (filter: string[][], groupBy: string[]) => void;

export interface CanopyFiltersProps extends ButtonProps {
    globalProps: GlobalProps;
    groupByFilters?: Record<string, Record<string, string>[]>;
    selectedGroupByFilters?: string[][];
    groupBy?: string[];
    selectedGroupBy?: string[];
    allowMultipleFilters?: boolean;
    defaultGroupBy?: string;
    selectAllFiltersOnGroupChange?: boolean;
    showGroupByFilters?: boolean;
    headerText?: string;
    onApply?: OnApply;
}

function Filters({
    globalProps,
    groupByFilters = {},
    selectedGroupByFilters = [],
    groupBy = [],
    selectedGroupBy = [''],
    allowMultipleFilters = true,
    defaultGroupBy = 'select-one',
    selectAllFiltersOnGroupChange = false,
    showGroupByFilters = true,
    headerText = 'ui.mm.filter-categories',
    onApply,
}: CanopyFiltersProps) {
    const [localSelectedGroupBy, setLocalSelectedGroupBy] = useState<string[]>([
        defaultGroupBy,
    ]);
    const [localSelectedGroupByFilters, setLocalSelectedGroupByFilters] =
        useState<string[][]>([]);
    const [currentFilters, setCurrentFilters] = useState<
        Record<string, string>[][]
    >([]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const btnRef = React.createRef();
    const currentColors = useCurrentColors();
    const { colorMode } = useColorMode();
    const { t } = globalProps;

    const updateFilters = () => {
        selectedGroupBy &&
            !isEqual(localSelectedGroupBy, selectedGroupBy) &&
            setLocalSelectedGroupBy(selectedGroupBy);
        selectedGroupBy?.forEach((item, index) => {
            if (selectedGroupByFilters[index]?.length > 0) {
                const filters = groupByFilters?.[item] && [
                    ...groupByFilters[item],
                ];
                setCurrentFilters((currentFilters) => {
                    currentFilters.splice(index, 1, [...filters]);
                    return currentFilters;
                });
            }
        });
        !isEqual(localSelectedGroupByFilters, selectedGroupByFilters) &&
            setLocalSelectedGroupByFilters([...selectedGroupByFilters]);
    };
    useEffect(() => {
        updateFilters();
    }, [selectedGroupBy, selectedGroupByFilters]);

    useEffect(() => {
        selectedGroupBy &&
            !isEqual(localSelectedGroupBy, selectedGroupBy) &&
            setLocalSelectedGroupBy(selectedGroupBy);
    }, [selectedGroupBy]);

    useEffect(() => {
        selectedGroupByFilters &&
            !isEqual(localSelectedGroupByFilters, selectedGroupByFilters) &&
            setLocalSelectedGroupByFilters([...selectedGroupByFilters]);
    }, [selectedGroupByFilters]);

    const handleGroupByChange = (item: any, index: number) => {
        //ts-ignore
        const _localSelectedGroupBy = cloneDeep(localSelectedGroupBy);
        const _currentFilters = cloneDeep(currentFilters);
        const _localSelectedGroupByFilters = cloneDeep(
            localSelectedGroupByFilters
        );
        _localSelectedGroupBy?.splice(index, 1, item.value);
        const filters = groupByFilters?.[item?.value] && [
            ...groupByFilters[item?.value],
        ];
        if (filters) {
            _currentFilters?.splice(index, 1, [...filters]);
            _localSelectedGroupByFilters?.splice(index, 1, []);

            setLocalSelectedGroupBy(_localSelectedGroupBy);
            setCurrentFilters(_currentFilters);

            if (selectAllFiltersOnGroupChange) {
                const selectedGroupByFilters =
                    groupByFilters[_localSelectedGroupBy[0]];
                const groupByFilterList =
                    selectedGroupByFilters?.map((g) => g.value) || [];
                setLocalSelectedGroupByFilters([groupByFilterList]);
            } else {
                setLocalSelectedGroupByFilters(_localSelectedGroupByFilters);
            }
        } else {
            setLocalSelectedGroupBy(_localSelectedGroupBy);
            setCurrentFilters([]);
            setLocalSelectedGroupByFilters([]);
        }
    };

    const handleFilterChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        const _localSelectedGroupByFilters = cloneDeep(
            localSelectedGroupByFilters
        );
        const filters = _localSelectedGroupByFilters[index];
        if (event.target.checked) {
            !filters.includes(event.target.value) &&
                filters.push(event.target.value);
        } else {
            filters.includes(event.target.value) &&
                filters.splice(filters.indexOf(event.target.value), 1);
        }
        _localSelectedGroupByFilters.splice(index, 1, filters);
        setLocalSelectedGroupByFilters(_localSelectedGroupByFilters);
    };

    const handleSelectAll = (
        event: React.ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        const filters = event.target.checked
            ? currentFilters[index].map(({ value }) => String(value))
            : [];
        const _localSelectedGroupByFilters = cloneDeep(
            localSelectedGroupByFilters
        );
        _localSelectedGroupByFilters.splice(index, 1, filters);
        setLocalSelectedGroupByFilters(_localSelectedGroupByFilters);
    };

    const handleResetFilter = () => {
        if (groupByFilters?.length) {
            const defaultGroupByFilters = groupByFilters[defaultGroupBy];
            const defaultGroupByFilterList =
                defaultGroupByFilters?.map((g) => g.value) || [];
            setLocalSelectedGroupBy([defaultGroupBy]);
            setLocalSelectedGroupByFilters([defaultGroupByFilterList]);
            if (defaultGroupByFilters) {
                setCurrentFilters([defaultGroupByFilters]);
            }
        } else {
            setLocalSelectedGroupBy([defaultGroupBy]);
            setCurrentFilters([]);
            setLocalSelectedGroupByFilters([]);
        }
    };

    const handleApplyFilter = () => {
        onClose();
        if (onApply) {
            localSelectedGroupByFilters &&
                localSelectedGroupBy &&
                onApply(localSelectedGroupByFilters, localSelectedGroupBy);
        }
    };

    const handleAddFilter = () => {
        const _localSelectedGroupBy = cloneDeep(localSelectedGroupBy);
        // const currentIndex = _localSelectedGroupBy?.length - 1;
        _localSelectedGroupBy?.push('select-one');
        setLocalSelectedGroupBy(_localSelectedGroupBy);
    };

    const getOptions = (
        groupBy: string[],
        localSelectedGroupBy: string[],
        index: number
    ) => {
        const options = groupBy?.reduce((acc: any, item: string) => {
            if (
                localSelectedGroupBy[index] === item ||
                !localSelectedGroupBy.includes(item)
            ) {
                acc?.push({
                    value: item,
                    label: t(`ui.mm.filter.${item}`),
                });
            }
            return acc;
        }, []);
        return options;
    };

    return (
        <>
            <Tooltip
                aria-label="page-filters"
                label={t('ui.mm.tooltip.filters')}
                placement="bottom-start"
                hasArrow>
                <IconButton
                    background="transparent"
                    cursor="pointer"
                    border="none"
                    borderRadius="4px"
                    aria-label="page-filters"
                    width="20px !important"
                    minWidth="20px !important"
                    height="20px !important"
                    onClick={() => {
                        updateFilters();
                        onOpen();
                    }}
                    icon={<ActFilter />}
                    backgroundColor="transparent"
                    color={currentColors.brand['300']}
                    variant="ghost"
                    _hover={{ backgroundColor: 'transparent' }}
                    _focus={{
                        border: '0px',
                        backgroundColor: 'transparent',
                        outline: 'none',
                    }}
                    _active={{ border: '0px' }}
                />
            </Tooltip>
            <Drawer
                isOpen={isOpen}
                placement="right"
                onClose={onClose}
                size="sm">
                <DrawerContent
                    px="17px"
                    py="17px"
                    height={{ xl: '100vh', sm: 'calc(100% - 10px)' }}
                    backgroundColor={currentColors.componentBg}>
                    <Flex direction="column">
                        <DrawerHeader
                            onClick={onClose}
                            header={t(headerText)}
                            logoHeader={t('ui.mm.page-filter')}></DrawerHeader>
                    </Flex>
                    <Flex
                        overflowY="auto"
                        direction="column"
                        marginBottom="30px"
                        justify="space-between">
                        {localSelectedGroupBy?.map((item, index) => {
                            return (
                                <Flex
                                    key={index}
                                    data-test="filter-area"
                                    direction="column"
                                    flexGrow={1}
                                    mt="15px"
                                    mb="15px">
                                    <Flex>
                                        <CanopySelect
                                            id={`filters${index}`}
                                            placeholder={t('select')}
                                            value={{
                                                value: localSelectedGroupBy[
                                                    index
                                                ],
                                                label: t(
                                                    `ui.mm.filter.${localSelectedGroupBy[index]}`
                                                ),
                                            }}
                                            options={getOptions(
                                                groupBy,
                                                localSelectedGroupBy,
                                                index
                                            )}
                                            onChange={(value: any) => {
                                                handleGroupByChange(
                                                    value,
                                                    index
                                                );
                                            }}
                                            height="35px"></CanopySelect>
                                    </Flex>
                                    {showGroupByFilters ? (
                                        <Flex
                                            direction="column"
                                            justify="space-between"
                                            flexGrow={1}>
                                            {' '}
                                            <Flex
                                                direction="column"
                                                flexGrow={1}>
                                                <Flex
                                                    mt="15px"
                                                    maxHeight={
                                                        allowMultipleFilters
                                                            ? '200px'
                                                            : 'none'
                                                    }
                                                    backgroundColor={
                                                        currentColors.brand[
                                                            '900'
                                                        ]
                                                    }
                                                    borderRadius="4px"
                                                    p="10px"
                                                    overflowY="auto"
                                                    width="100%">
                                                    {currentFilters?.[0]
                                                        ?.length ? (
                                                        <Flex
                                                            direction="column"
                                                            width="100%">
                                                            {item !==
                                                            'select-one' ? (
                                                                <>
                                                                    <Checkbox
                                                                        colorScheme={
                                                                            colorMode +
                                                                            'Primary'
                                                                        }
                                                                        key={
                                                                            'select-all'
                                                                        }
                                                                        isChecked={
                                                                            localSelectedGroupByFilters?.[
                                                                                index
                                                                            ]
                                                                                ?.length ===
                                                                            currentFilters?.[
                                                                                index
                                                                            ]
                                                                                ?.length
                                                                        }
                                                                        isIndeterminate={
                                                                            localSelectedGroupByFilters?.[
                                                                                index
                                                                            ]
                                                                                ?.length !==
                                                                                currentFilters?.[
                                                                                    index
                                                                                ]
                                                                                    ?.length &&
                                                                            localSelectedGroupByFilters?.[
                                                                                index
                                                                            ]
                                                                                ?.length !==
                                                                                0
                                                                        }
                                                                        fontWeight={
                                                                            localSelectedGroupByFilters?.[
                                                                                index
                                                                            ]
                                                                                ?.length ===
                                                                            currentFilters?.length
                                                                                ? '600'
                                                                                : 'normal'
                                                                        }
                                                                        height="18px"
                                                                        mb="15px"
                                                                        onChange={(
                                                                            value: React.ChangeEvent<HTMLInputElement>
                                                                        ) => {
                                                                            handleSelectAll(
                                                                                value,
                                                                                index
                                                                            );
                                                                        }}>
                                                                        {t(
                                                                            'select-all'
                                                                        )}
                                                                    </Checkbox>

                                                                    <VStack
                                                                        width="100%"
                                                                        align="flex-start"
                                                                        fontSize="15px">
                                                                        {currentFilters?.[
                                                                            index
                                                                        ]?.map(
                                                                            (
                                                                                filterItem: Record<
                                                                                    string,
                                                                                    string
                                                                                >
                                                                            ) => {
                                                                                return (
                                                                                    <Checkbox
                                                                                        width="100%"
                                                                                        colorScheme={
                                                                                            colorMode +
                                                                                            'Primary'
                                                                                        }
                                                                                        key={String(
                                                                                            filterItem.value
                                                                                        )}
                                                                                        color={
                                                                                            currentColors
                                                                                                .brand[10]
                                                                                        }
                                                                                        value={String(
                                                                                            filterItem.value
                                                                                        )}
                                                                                        onChange={(
                                                                                            event: React.ChangeEvent<HTMLInputElement>
                                                                                        ) =>
                                                                                            handleFilterChange(
                                                                                                event,
                                                                                                index
                                                                                            )
                                                                                        }
                                                                                        isChecked={localSelectedGroupByFilters[
                                                                                            index
                                                                                        ]?.includes(
                                                                                            String(
                                                                                                filterItem.value
                                                                                            )
                                                                                        )}
                                                                                        fontWeight={
                                                                                            localSelectedGroupByFilters?.[
                                                                                                index
                                                                                            ]?.find(
                                                                                                (
                                                                                                    key
                                                                                                ) => {
                                                                                                    return (
                                                                                                        key ===
                                                                                                        String(
                                                                                                            filterItem.value
                                                                                                        )
                                                                                                    );
                                                                                                }
                                                                                            )
                                                                                                ? '600'
                                                                                                : 'normal'
                                                                                        }
                                                                                        borderColor={
                                                                                            currentColors
                                                                                                .brand[
                                                                                                '700'
                                                                                            ]
                                                                                        }
                                                                                        height="30px"
                                                                                        fontSize="15px">
                                                                                        <Box
                                                                                            width="250px"
                                                                                            textOverflow="ellipsis"
                                                                                            overflow="hidden"
                                                                                            whiteSpace="nowrap">
                                                                                            {
                                                                                                filterItem.label
                                                                                            }
                                                                                        </Box>
                                                                                    </Checkbox>
                                                                                );
                                                                            }
                                                                        )}
                                                                    </VStack>
                                                                </>
                                                            ) : (
                                                                t(
                                                                    'ui.mm.filter.select-one-description'
                                                                )
                                                            )}
                                                        </Flex>
                                                    ) : (
                                                        t(
                                                            'ui.mm.filter.no-options'
                                                        )
                                                    )}
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                    ) : null}
                                </Flex>
                            );
                        })}
                        {allowMultipleFilters && (
                            <CanopyButton
                                onClick={handleAddFilter}
                                data-test="add-filter"
                                alignSelf="flex-start"
                                pt="7px"
                                fontSize="sm"
                                variant="ghost"
                                mt="20px"
                                pb="7px"
                                mb="20px"
                                disabled={
                                    localSelectedGroupBy?.length >=
                                        groupBy?.length ||
                                    localSelectedGroupBy.includes(
                                        'select-one'
                                    ) ||
                                    (localSelectedGroupByFilters?.length !==
                                        0 &&
                                        localSelectedGroupByFilters.findIndex(
                                            (item) => item.length === 0
                                        ) !== -1)
                                }
                                color={currentColors.primaryColor}>
                                <AddIcon
                                    width={'12px !important'}
                                    height={'12px !important'}
                                    mr="4px"
                                />
                                {t('ui.mm.filters.add-filter')}
                            </CanopyButton>
                        )}
                    </Flex>

                    <Flex
                        position="absolute"
                        bottom="0"
                        direction="column"
                        marginBottom="6px"
                        width="100%">
                        <Flex
                            direction="column"
                            width="100%">
                            <Divider
                                borderColor={currentColors.brand['700']}
                                width="100%"
                                my="10px"
                            />
                            <Flex>
                                <CanopyButton
                                    width="154px"
                                    fontSize="sm"
                                    variant="outline"
                                    onClick={handleResetFilter}
                                    disabled={
                                        localSelectedGroupBy[0] === 'select-one'
                                    }
                                    mr="11.5px">
                                    {t('reset')}
                                </CanopyButton>
                                <CanopyButton
                                    isDisabled={
                                        (isEqual(
                                            selectedGroupByFilters,
                                            localSelectedGroupByFilters
                                        ) &&
                                            isEqual(
                                                selectedGroupBy,
                                                localSelectedGroupBy
                                            )) ||
                                        localSelectedGroupByFilters.findIndex(
                                            (item) => item.length === 0
                                        ) !== -1
                                    }
                                    width="154px"
                                    height="35px"
                                    fontSize="sm"
                                    onClick={handleApplyFilter}>
                                    {t('apply-filter')}
                                </CanopyButton>
                            </Flex>
                        </Flex>
                    </Flex>
                </DrawerContent>
            </Drawer>
        </>
    );
}
export default Filters;
